import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getDocument, saveDocument, updateDocument } from './DocumentService';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import SystemDashboard from 'layouts/SystemDashboard';
import Container from 'components/Container';

const validationSchema = yup.object({
  name: yup.string().required('Name is required.'),
  description: yup.string().required('Description is required.'),
  startDate: yup.date().nullable().required('Start date is required.'),
  endDate: yup
    .date()
    .nullable()
    .min(yup.ref('startDate'), 'End date cannot be before start date')
    .required('End date is required.'),
});

const DocumentDetail = (): JSX.Element => {
  const { documentid } = useParams<{ documentid: string }>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      contactPerson: '',
      location: '',
      status: '',
      startDate: null as Date | null,
      endDate: null as Date | null,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const document = {
        ...values,
        startDate: values.startDate
          ? new Date(values.startDate).toISOString()
          : null,
        endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
      };

      if (documentid) {
        updateDocument(parseInt(documentid), document)
          .then(() => navigate('/system/dashboard/documents'))
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      } else {
        saveDocument(document)
          .then(() => navigate('/system/dashboard/documents'))
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      }
    },
  });

  useEffect(() => {
    if (documentid) {
      getDocument(parseInt(documentid))
        .then((response) => {
          const data = response.data;
          formik.setValues({
            name: data.name,
            description: data.description,
            contactPerson: data.contactPerson,
            location: data.location,
            status: data.status,
            startDate: data.startDate ? new Date(data.startDate) : null,
            endDate: data.endDate ? new Date(data.endDate) : null,
          });
        })
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }, [documentid]);

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);

    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the date is invalid
    }

    // Format date for input (YYYY-MM-DDTHH:mm)
    return date.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/sections"
            >
              Documents
            </Link>
            <Typography color="text.primary">Document Detail</Typography>
          </Breadcrumbs>
        </Box>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Document Detail</Typography>
        </Box>
        <Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  name="contactPerson"
                  value={formik.values.contactPerson}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={formik.values.startDate || ''}
                  onChange={(e) =>
                    formik.setFieldValue('startDate', e.target.value)
                  }
                  fullWidth
                  error={
                    formik.touched.startDate && Boolean(formik.errors.startDate)
                  }
                  // helperText={
                  //   formik.touched.startDate && formik.errors.startDate
                  // }
                  InputLabelProps={{
                    shrink: true, // Ensures the label floats when there is a value
                  }}
                  InputProps={{
                    value: formatDateForInput(formik.values.startDate), // Properly format for display
                  }}
                  // style={{
                  //   visibility: formik.values.startDate ? 'visible' : 'hidden',
                  // }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={formik.values.endDate || ''}
                  onChange={(e) =>
                    formik.setFieldValue('endDate', e.target.value)
                  }
                  fullWidth
                  error={
                    formik.touched.endDate && Boolean(formik.errors.endDate)
                  }
                  //helperText={formik.touched.endDate && formik.errors.endDate}
                  InputLabelProps={{
                    shrink: true, // Ensures the label floats when there is a value
                  }}
                  InputProps={{
                    value: formatDateForInput(formik.values.endDate), // Properly format for display
                  }}
                  // style={{
                  //   visibility: formik.values.startDate ? 'visible' : 'hidden',
                  // }}
                />
              </Grid>
              <Grid item container xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  marginBottom={4}
                >
                  <Button
                    onClick={() => navigate('/system/dashboard/documents')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" color="primary">
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default DocumentDetail;
